import { dateBetweenWithoutYear } from "@omnia/shared/utils/date"
import { ICropping, IHolding, RegionId, setDates } from "../omnia"
import { IClosedPeriod, IClosedPeriodFertiliser, IClosedPeriodManure, IClosedPeriodResult, IFieldLimitResult, IManureStock, INutritionPlanProductApp, ISoilTypeService, NVZCropGroup } from "../types"
import { checkLightOrShallowSoil } from "../utils"

const winterCerealsCropsIds = [1, 3, 4, 8, 10, 96]
const winterOsrCropsIds = [6, 50, 56, 83, 84]


interface FertRules {
    Region: RegionId
    Manure: false
    Rules: IClosedPeriodFertiliser[]
}

interface ManureRules {
    Region: RegionId
    Manure: true
    Rules: IClosedPeriodManure[]
}

export type ClosedPeriodRules = ManureRules | FertRules


export interface ClosedPeriodProvider {

    getClosedPeriod(cropping: ICropping, holding: IHolding, date: Date, manure?: IManureStock): IClosedPeriodResult

}

export function createClosedPeriodProvider(rules: ClosedPeriodRules[], soilTypeService: ISoilTypeService, region: RegionId): ClosedPeriodProvider {

    let fertRules: IClosedPeriodFertiliser[]
    let manureRules: IClosedPeriodManure[]

    rules = setupRules(rules, region)

    fertRules = (rules.find(r => (!r.Manure))?.Rules || []) as IClosedPeriodFertiliser[]
    manureRules = (rules.find(r => (!!r.Manure))?.Rules || []) as IClosedPeriodManure[]

    return {
        getClosedPeriod
    }

    ///

    function getClosedPeriod(cropping: ICropping, holding: IHolding, date: Date, manure?: IManureStock): IClosedPeriodResult {

        let result: IClosedPeriodResult = {
            closed: false
        }
        if (!cropping.Field.NVZ) {
            return result
        }

        const manureType = manure ? manure.NvzManureType ? manure.NvzManureType : manure.ManureType : undefined

        const selectedClosedPeriod = manureType
            ? getClosedPeriodForManure(cropping, manureType.InvariantId)
            : getClosedPeriodForFert(holding, cropping)

        if (selectedClosedPeriod && (dateBetweenWithoutYear(selectedClosedPeriod.StartDate, selectedClosedPeriod.EndDate, date) || (!!selectedClosedPeriod.MaxRateToMarch && date.getMonth() < 2))) {

            result = {
                closed: true,
                expemption: selectedClosedPeriod.Expemption,
                maxRate: selectedClosedPeriod.MaxRate,
                maxRateToMarch: selectedClosedPeriod.MaxRateToMarch,
                startDate: selectedClosedPeriod.StartDate,
                endDate: selectedClosedPeriod.EndDate,
                specificStartDate: selectedClosedPeriod.SpecificStartDate
            }

        }

        return result

    }


    function getClosedPeriodForFert(holding: IHolding, cropping: ICropping) {

        if (holding.Region === RegionId.Scotland) {

            const cityClosedPeriod = fertRules.find(c => c.City === holding.Town.Name)

            if (cityClosedPeriod) {
                const period = cityClosedPeriod.ClosedPeriodFertiliserItems.find(c => c.Grass === !!cropping.GrassManagement)
                return period?.ClosedPeriod
            }

        } else {


            const defaultPeriod = fertRules.find(c => !c.City)
            const specificCrop = defaultPeriod.ClosedPeriodFertiliserItems.find(c => c.CropId === cropping.CropId)
            if (specificCrop) {
                return specificCrop.ClosedPeriod
            }

            const period = defaultPeriod.ClosedPeriodFertiliserItems.find(c => c.Grass === !!cropping.GrassManagement)
            return period?.ClosedPeriod

        }

    }

    function getClosedPeriodForManure(cropping: ICropping, manureTypeId: number) {

        const lightAndShallow = checkLightOrShallowSoil(soilTypeService, cropping)

        var cropGroup = getCropGroup(cropping)

        var selectedClosedPeriod: IClosedPeriod = null
        var closedPeriodsManure = manureRules.find(c => c.ManureTypeId === manureTypeId)

        if (closedPeriodsManure) {

            let closedPeriods = closedPeriodsManure.ClosedPeriodManureItems.filter(c =>
                (lightAndShallow === c.LightAndShallow) &&
                (cropGroup === c.CropGroup) &&
                (!c.MaxSowingDate || !!c.MaxSowingDate === (!!c.MaxSowingDate && dayMonthComparer(c.MaxSowingDate, cropping.SowingDate) >= 0)))

            if (!closedPeriods.length) {
                closedPeriods = closedPeriodsManure.ClosedPeriodManureItems.filter(c =>
                    (lightAndShallow === c.LightAndShallow) &&
                    (c.CropGroup === NVZCropGroup.Other) &&
                    (!c.MaxSowingDate || !!c.MaxSowingDate === (!!c.MaxSowingDate && dayMonthComparer(c.MaxSowingDate, cropping.SowingDate) >= 0)))
            }

            if (closedPeriods.length > 1) {
                // Take the closed period with the smallest MaxSowingDate

                closedPeriods = closedPeriods.sort((a, b) => {

                    let diff = (a.MaxSowingDate ? 0 : 1) - (b.MaxSowingDate ? 0 : 1)
                    if (!diff) {
                        diff = a.MaxSowingDate.valueOf() - b.MaxSowingDate.valueOf()
                    }

                    return diff

                })

                selectedClosedPeriod = closedPeriods[0].ClosedPeriod

            } else if (closedPeriods.length === 1) {
                selectedClosedPeriod = closedPeriods[0].ClosedPeriod
            } else {
                // If no match, take the default
                selectedClosedPeriod = closedPeriodsManure.ClosedPeriodManureItems.find(c => !c.MaxSowingDate && c.CropGroup === NVZCropGroup.Other && !c.LightAndShallow).ClosedPeriod
            }

        }

        return selectedClosedPeriod

    }

}


export function getFieldLimit(application: INutritionPlanProductApp, cropping: ICropping, holding: IHolding): IFieldLimitResult {

    const compostIds = [32, 33, 34]
    const compostCropIds = [26, 27, 60]

    const manure = application.Manure
    const manureType = manure?.NvzManureType ?? manure?.ManureType

    const scotland = (holding.Region == RegionId.Scotland)

    // Specific rules for compost, not applicable to Scotland
    if (manureType && compostIds.some(id => id == manureType.InvariantId) && (!scotland)) {

        // Apples, Pears and Cherries: 1000kg, rolling 4 years
        if (compostCropIds.indexOf(cropping.CropId) !== -1) {
            return {
                yearCount: 4,
                maxRate: 1000
            }
        }

        // Other crops: 500kg, rolling 2 years
        return {
            yearCount: 2,
            maxRate: 500

        }
    }

    return {
        yearCount: 1,
        maxRate: 250
    }

}

function getCropGroup(cropping: ICropping): NVZCropGroup {

    if (!!cropping.GrassManagement) {
        return NVZCropGroup.Grass
    } else if (winterCerealsCropsIds.indexOf(cropping.CropId) !== -1) {
        return NVZCropGroup.WinterCereals
    } else if (winterOsrCropsIds.indexOf(cropping.CropId) !== -1) {
        return NVZCropGroup.WinterOsrAndCovers
    }

    return NVZCropGroup.Other
}

function dayMonthComparer(a: Date, b: Date): number {

    var aDay = a.getDate()
    var aMonth = a.getMonth()
    var bDay = b.getDate()
    var bMonth = b.getMonth()

    if (aMonth > bMonth) {
        return 1
    }

    if (aMonth < bMonth) {
        return -1
    }

    if (aDay > bDay) {
        return 1
    }

    if (aDay < bDay) {
        return -1
    }

    return 0
}

function setupRules(rules: ClosedPeriodRules[], region: RegionId) {

    rules = rules.filter(r => r.Region == region)

    for (const set of rules) {

        set.Manure = !!set.Manure

        if (set.Manure == true) {

            for (const r of set.Rules) {
                setDates(r.ClosedPeriodManureItems, ["MaxSowingDate"], true)
                r.ClosedPeriodManureItems.forEach(p => setDates([p.ClosedPeriod], ["StartDate", "EndDate"], true))
            }

        }

        if (set.Manure == false) {

            for (const r of set.Rules) {
                r.ClosedPeriodFertiliserItems.forEach(p => setDates([p.ClosedPeriod], ["StartDate", "EndDate", "SpecificStartDate"], true))
            }

        }

    }

    return rules

}
